import React, { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { isNotEmptyAny, throttle, percentValueFormatter, isNotEmpty } from '@lhb/func';
import { themeColor } from '../../config-v2';
import { EChartsOption } from 'echarts';
import styles from './index.module.less';
import { unstable_batchedUpdates } from 'react-dom';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';

echarts.use([
  SVGRenderer,
  PieChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

export interface V2PieChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 基础类型或者环形
   * @default base
   */
  type?: 'base' | 'circle' | 'rose',
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 自定义颜色主题
   */
  colors?: string[];
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description 是否跟随外容器自动缩放
   * @default false
   */
  resizable?: boolean;
  /**
   * @description y轴数据 更多配置见 https://echarts.apache.org/zh/option.html#series-pie
   */
  seriesData: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;
  /**
   * @description 图例文字最大长度
   */
  legendTextMaxLength?: number;
}

/**
* @description 便捷文档地址
* @see https://reactpc.lanhanba.net/components/charts/v2pie-chart
*/

const V2PieChart: React.FC<V2PieChartProps> = (props) => {
  const {
    title,
    theme = 'blue',
    type = 'base',
    width,
    height = 270,
    resizable = false,
    seriesData = [],
    config = {},
    wrapperClassName,
    legendTextMaxLength = 6,
    colors,
  } = props;
  const chartRef = useRef<any>(null);
  const [echartsInstance, setEchartsInstance] = useState<any>(null);

  const seriesBaseConfig = {
    type: 'pie',
    radius: type === 'circle' ? ['38%', '50%'] : '50%',
    center: ['30%', '50%'],
    roseType: type === 'rose' ? 'area' : false,
    itemStyle: {
      borderRadius: 3,
      borderColor: '#fff',
      borderWidth: 1,
    },
    label: {
      show: false,
    },
    labelLine: {
      show: false,
    },
    tooltip: {
      position: 'inside',
    },
  };

  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({});

  useEffect(() => {
    methods.updateOptions();
  }, [seriesData]);

  useEffect(() => {
    return () => echartsInstance?.dispose();
  }, []);

  useEffect(() => {
    if (!options || !Object.keys(options).length) {
      return;
    }
    if (isConfig && echartsInstance) { // 如果已配置，更新图表
      echartsInstance?.setOption(options);
    } else { // 未配置，初始化图表
      if (chartRef.current) {
        const chart = echarts.init(chartRef.current, 'light', {
          renderer: 'svg',
          height,
        });
        chart.setOption(options);
        setEchartsInstance(chart);
      }
    }
  }, [options, isConfig]);

  useEffect(() => {
    if (echartsInstance && resizable) {
      window.addEventListener('resize', throttle(() => {
        echartsInstance.resize(); // 图表根据窗口大小自适应
      }, 300));
    }
    return () => {
      if (resizable) {
        window.removeEventListener('resize', throttle(() => {
          echartsInstance.resize();
        }, 300));
      }
    };
  }, [echartsInstance, resizable]);

  const methods = useMethods({

    /** 获取默认配置 */
    getDefaultOptions() {
      return {
        ...config,
        title: {
          show: !!title,
          text: title || '',
          textStyle: {
            color: '#222222',
            fontWeight: 500,
            fontFamily: 'PingFangSC-Medium, PingFang SC',
            fontSize: 16,
            lineHeight: 22,
          },
          top: 6,
          left: 6,
          ...(config.title || {}),
        },
        legend: {
          type: 'plain',
          width: '45%',
          orient: 'vertical',
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 8,
          itemStyle: {
            borderWidth: 0,
          },
          textStyle: {
            fontSize: 13,
            fontWeight: 400,
            color: '#666666',
            lineHeight: 14,
            rich: {
              a: {
                verticalAlign: 'middle',
              },
            },
            padding: [0, 4, 1, 0],
          },
          icon: 'roundRect',
          top: 'center',
          left: '55%',
          tooltip: {
            show: true,
            trigger: 'item',
          },
          ...(config.legend || {}),
        },
        color: colors || themeColor[theme],
        grid: {
          left: 50,
          bottom: 36,
          right: 12,
          ...(config.grid || {}),
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: '#222',
          borderColor: '#222',
          borderWidth: 0,
          textStyle: {
            color: '#fff',
            fontWeight: 'normal',
            fontSize: 12,
            fontFamily: 'PingFangSC-Regular, PingFang SC',
          },
          confine: true,
          ...(config.tooltip || {}),
        },
        series: [],
      };
    },

    /** 更新配置 */
    updateOptions() {
      if (seriesData?.length) {
        const totalValue = seriesData[0]?.data?.reduce((prevVal: number, currVal: any) => {
          return prevVal + currVal.value;
        }, 0); // 饼图的value总和，用于计算每块图例百分比
        unstable_batchedUpdates(() => {
          const defaultOptions = methods.getDefaultOptions();
          setOptions({
            ...defaultOptions,
            series: seriesData.map((item) => {
              return {
                ...seriesBaseConfig,
                tooltip: {
                  valueFormatter: (value: number) => {
                    const percent = percentValueFormatter(value, totalValue);
                    if (isNotEmpty(percent)) {
                      return `${percent}%`;
                    }
                    return '-';
                  }
                },
                ...item,
              };
            }),
            legend: {
              formatter: (name: string) => {
                const data = seriesData[0]?.data || [];
                const unit = seriesData[0]?.unit || '';
                const item = data.find(item => item.name === name);
                const newName = name?.length > legendTextMaxLength ? name.slice(0, legendTextMaxLength) + '...' : name;
                if (isNotEmptyAny(item)) {
                  return `${newName} ${item.value}${unit}`;
                }
                return newName;
              },
              ...defaultOptions.legend, // 不使用 options.legend 是避免旧的覆盖新的 seriesData 生成的 formatter，同时又需要 config.legend 生效
            },
          });
          setIsConfig(true);
        });
      }
    },

  });

  return (
    <div className={cs(styles.V2PieChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={cs(styles.V2PieChart, 'V2PieChart')}></div>
    </div>
  );
};

export default V2PieChart;
